import { formatDateTime } from "@/lib/date-format";
import {
  flattenSocialPostSceneBlocks,
  getSceneImageFitLayout,
  toKonvaImageFrameProps,
  toKonvaRectNodeProps,
  toKonvaTextNodeProps,
} from "@runcheetah/shared";

import type { SceneBlock, SceneBrandkit, ScenePlan, SocialPostGenerationTaskDetail } from "./types";

export const formatDate = formatDateTime;

export function formatDuration(createdAtIso: string, updatedAtIso: string): string {
  const createdMs = new Date(createdAtIso).getTime();
  const updatedMs = new Date(updatedAtIso).getTime();
  const durationMs = Math.max(0, updatedMs - createdMs);

  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }

  const durationSec = durationMs / 1000;
  if (durationSec < 60) {
    return `${durationSec.toFixed(1)}s`;
  }

  return `${(durationSec / 60).toFixed(1)}m`;
}

export const getSceneBlocksFlat = (plan: ScenePlan): SceneBlock[] =>
  flattenSocialPostSceneBlocks(plan.scene.blocks) as SceneBlock[];

export const getKonvaImageFitLayout = getSceneImageFitLayout;
export { toKonvaImageFrameProps, toKonvaRectNodeProps, toKonvaTextNodeProps };

export const getBlockStatus = (
  block: SceneBlock,
  imageAssetByBlockId: Map<string, SocialPostGenerationTaskDetail["linkedAssets"][number]>,
  brandkitLogo: SceneBrandkit["logo"],
): "ready" | "pending" | "legacy" => {
  if (block.type === "group") {
    const childStatuses = block.children.map((child) =>
      getBlockStatus(child as SceneBlock, imageAssetByBlockId, brandkitLogo),
    );
    if (childStatuses.some((status) => status === "pending")) {
      return "pending";
    }

    if (childStatuses.some((status) => status === "legacy")) {
      return "legacy";
    }

    return "ready";
  }

  if (block.type === "image") {
    return imageAssetByBlockId.has(block.id) ? "ready" : "pending";
  }

  if (block.type === "logo") {
    if (!block.assetObjectKey) {
      return "legacy";
    }

    return brandkitLogo?.objectKey === block.assetObjectKey ? "ready" : "pending";
  }

  return "ready";
};

export const getKonvaPropsPreview = (block: SceneBlock): Record<string, unknown> => {
  if (block.type === "rect") {
    return {
      node: "Rect",
      props: toKonvaRectNodeProps(block, 1),
    };
  }

  if (block.type === "text") {
    return {
      node: "Text",
      props: toKonvaTextNodeProps(block, 1),
    };
  }

  if (block.type === "image" || block.type === "logo") {
    return {
      node: "Image",
      props: toKonvaImageFrameProps(block, 1),
      fitAdapter: {
        fit: block.fit,
        note: "cover/contain/fill are adapted into Konva image x/y/width/height/crop based on source image dimensions.",
      },
    };
  }

  if (block.type === "shape") {
    return getShapeKonvaPropsPreview(block);
  }

  if (block.type === "frame") {
    return {
      node: "Frame",
      fillProps: block.fill
        ? {
            x: block.x,
            y: block.y,
            width: block.width,
            height: block.height,
            fill: block.fill,
            opacity: block.opacity,
            rotation: block.rotation,
            cornerRadius: block.cornerRadius,
          }
        : null,
      strokeProps: {
        x: block.x + block.insetPadding,
        y: block.y + block.insetPadding,
        width: Math.max(0, block.width - block.insetPadding * 2),
        height: Math.max(0, block.height - block.insetPadding * 2),
        stroke: block.stroke,
        strokeWidth: block.strokeWidth,
        opacity: block.opacity,
        rotation: block.rotation,
        cornerRadius: block.cornerRadius,
      },
    };
  }

  return {
    node: "Group",
    props: block,
  };
};

const getShapeKonvaPropsPreview = (
  block: Extract<SceneBlock, { type: "shape" }>,
): Record<string, unknown> => {
  const x = block.x;
  const y = block.y;
  const width = block.width;
  const height = block.height;
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const radius = Math.min(width, height) / 2;
  const common = {
    fill: block.fill,
    stroke: block.stroke,
    strokeWidth: block.strokeWidth,
    opacity: block.opacity,
    rotation: block.rotation,
    dash: block.dash,
    lineCap: block.lineCap,
    lineJoin: block.lineJoin,
  };

  if (block.shapeType === "ellipse") {
    return {
      node: "Ellipse",
      props: { x: centerX, y: centerY, radiusX: width / 2, radiusY: height / 2, ...common },
    };
  }

  if (block.shapeType === "line") {
    const localPoints = block.points ?? [0, 0, block.width, block.height];
    return {
      node: "Line",
      props: {
        points: localPoints.map((value, idx) => value + (idx % 2 === 0 ? x : y)),
        closed: block.closed,
        tension: block.tension,
        ...common,
      },
    };
  }

  if (block.shapeType === "polygon") {
    return {
      node: "RegularPolygon",
      props: {
        x: centerX,
        y: centerY,
        sides: block.sides ?? 6,
        radius: block.radius ?? radius,
        ...common,
      },
    };
  }

  if (block.shapeType === "star") {
    return {
      node: "Star",
      props: {
        x: centerX,
        y: centerY,
        numPoints: block.sides ?? 5,
        innerRadius: block.innerRadius ?? radius / 2,
        outerRadius: block.outerRadius ?? radius,
        ...common,
      },
    };
  }

  if (block.shapeType === "ring") {
    return {
      node: "Ring",
      props: {
        x: centerX,
        y: centerY,
        innerRadius: block.innerRadius ?? radius / 2,
        outerRadius: block.outerRadius ?? radius,
        ...common,
      },
    };
  }

  if (block.shapeType === "arc") {
    return {
      node: "Arc",
      props: {
        x: centerX,
        y: centerY,
        innerRadius: block.innerRadius ?? radius / 2,
        outerRadius: block.outerRadius ?? radius,
        angle: block.angle ?? 120,
        clockwise: block.clockwise,
        ...common,
      },
    };
  }

  return {
    node: "Path",
    props: { x, y, scaleX: 1, scaleY: 1, data: block.pathData ?? "M0 0", ...common },
  };
};
